import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import config from '../config';

function McpKeySettings() {
  const { getAuthHeaders } = useAuth();
  const [keyStatus, setKeyStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const fetchKeyStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${config.API_BASE_URL}/auth/mcp-key`, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load key status');
      setKeyStatus(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchKeyStatus(); }, [fetchKeyStatus]);

  const handleGenerate = async () => {
    if (keyStatus?.has_key && !window.confirm('Replace your existing key? The old key stops working immediately.')) return;
    setGenerating(true); setError(null); setNewKey(null);
    try {
      const res = await fetch(`${config.API_BASE_URL}/auth/mcp-key`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate key');
      setNewKey(data.api_key);
      setKeyStatus({ has_key: true, created_at: data.created_at, key_preview: data.api_key.slice(0, 4) + '****' });
    } catch (err) { setError(err.message); }
    finally { setGenerating(false); }
  };

  const handleRevoke = async () => {
    if (!window.confirm('Revoke your MCP API key?')) return;
    setRevoking(true); setError(null); setNewKey(null);
    try {
      const res = await fetch(`${config.API_BASE_URL}/auth/mcp-key`, {
        method: 'DELETE', headers: { ...getAuthHeaders() },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to revoke');
      setKeyStatus({ has_key: false });
    } catch (err) { setError(err.message); }
    finally { setRevoking(false); }
  };

  const handleCopy = async () => {
    if (!newKey) return;
    await navigator.clipboard.writeText(newKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-kaleido-gray-500">
        Connect Kal to AI clients like Claude Desktop or Cursor with a personal API key.
      </p>

      {error && (
        <div className="flex items-start gap-1.5 p-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-xs">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0 mt-0.5">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {error}
        </div>
      )}

      {/* Status */}
      {loading ? (
        <div className="flex items-center gap-1.5 text-xs text-kaleido-gray-400">
          <div className="w-3 h-3 border-2 border-kaleido-purple/20 border-t-kaleido-purple rounded-full animate-spin"/>
          Checking...
        </div>
      ) : keyStatus?.has_key ? (
        <div className="flex items-center gap-1.5 text-xs">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500"/>
          <span className="text-kaleido-gray-600 font-medium">Active:</span>
          <span className="font-mono text-kaleido-gray-400">{keyStatus.key_preview}</span>
          {keyStatus.created_at && (
            <span className="text-kaleido-gray-400 text-[10px]">
              ({new Date(keyStatus.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })})
            </span>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-1.5 text-xs text-kaleido-gray-400">
          <span className="w-1.5 h-1.5 rounded-full bg-gray-300"/>
          No key generated.
        </div>
      )}

      {/* New key reveal */}
      {newKey && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 space-y-2">
          <p className="text-xs text-amber-800 font-medium">Copy now — won't be shown again.</p>
          <div className="flex items-center gap-1.5">
            <code className="flex-1 bg-white border border-amber-200 rounded px-2 py-1.5 text-[10px] font-mono text-kaleido-gray-800 break-all select-all">
              {newKey}
            </code>
            <button onClick={handleCopy}
              className="shrink-0 px-2 py-1.5 rounded text-[10px] font-medium border border-kaleido-purple/30 text-kaleido-purple hover:bg-kaleido-purple/10 transition-colors">
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-1.5">
        <button onClick={handleGenerate} disabled={generating || revoking}
          className="px-3 py-1.5 rounded-md text-xs font-medium text-white bg-kaleido-purple hover:bg-kaleido-purple/90 disabled:opacity-50 transition-colors">
          {generating ? 'Generating...' : keyStatus?.has_key ? 'Regenerate' : 'Generate Key'}
        </button>
        {keyStatus?.has_key && (
          <button onClick={handleRevoke} disabled={generating || revoking}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-red-600 border border-red-200 hover:bg-red-50 disabled:opacity-50 transition-colors">
            {revoking ? 'Revoking...' : 'Revoke'}
          </button>
        )}
      </div>

      {/* Hint */}
      <p className="text-[10px] text-kaleido-gray-400 pt-1 border-t border-kaleido-gray-100">
        Use as <code className="font-mono bg-kaleido-purple/8 px-0.5 rounded">KAL_API_KEY</code> in your MCP client.{' '}
        <a href="https://github.com/Proj-Analytix/Hank/blob/main/docs/operations/mcp-server.md"
          target="_blank" rel="noopener noreferrer"
          className="text-kaleido-purple underline underline-offset-2 hover:opacity-75">Setup guide</a>
      </p>
    </div>
  );
}

export default McpKeySettings;
